/*
 * FROSTBEULE · Decks (owner: CARDS) – <fb-decks> custom element.
 * - Comparison slider: the Unartig layer is clipped from position p (0…1, share of Artig) to the right.
 *   Pointer: mouse click/drag anywhere on the stage (click jumps), touch drags relative after a small
 *   horizontal move (vertical swipes keep scrolling the page, touch-action: pan-y). Keyboard on the
 *   role="slider" handle: arrows ±5 %, PageUp/PageDown ±20 %, Home/End. Mobile switch buttons move it too.
 * - Spring: critically damped while dragging, soft overshoot on release (fling keeps a little velocity).
 *   Each side centres its content in its visible area, grows slightly and fans out its example fronts;
 *   the narrow side fades its text. Transforms are written directly (no per-frame CSS variables).
 * - First view: a gentle nudge shows that the slider moves (motion full, until the first interaction).
 * - Mix row: two stacks riffle into one when scrolled into view (WAAPI), replay via button/click.
 * Motion: full = springs + riffle; soft = no overshoot, no nudge/riffle; none = instant positions.
 */
(function () {
  'use strict';

  if (!('customElements' in window) || customElements.get('fb-decks')) return;

  var MOBILE = '(max-width: 749px)';
  var EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
  var EASE_IN_OUT = 'cubic-bezier(0.65, 0, 0.35, 1)';

  function motion() {
    if (window.FROSTBEULE && typeof window.FROSTBEULE.motion === 'function') return window.FROSTBEULE.motion();
    return document.documentElement.getAttribute('data-motion') || 'full';
  }

  function clamp(v, min, max) {
    return Math.min(max, Math.max(min, v));
  }

  function smooth(t) {
    return t * t * (3 - 2 * t);
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function num(el, prop) {
    return parseFloat(el.style.getPropertyValue(prop)) || 0;
  }

  class FbDecks extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.stage = this.querySelector('[data-stage]');
      this.layerU = this.querySelector('[data-layer="unartig"]');
      this.handle = this.querySelector('[data-handle]');
      if (!this.stage || !this.layerU || !this.handle) return;
      this._bound = true;

      this.sides = ['artig', 'unartig'].map(
        function (name) {
          var el = this.querySelector('[data-content="' + name + '"]');
          return {
            el: el,
            visual: el && el.querySelector('[data-visual]'),
            text: el && el.querySelector('[data-text]'),
            frontL: el && el.querySelector('[data-front="l"]'),
            frontR: el && el.querySelector('[data-front="r"]'),
          };
        }.bind(this)
      );
      this.switchBtns = toArray(this.querySelectorAll('[data-show]'));
      this.riffle = this.querySelector('[data-riffle]');
      this.rcards = toArray(this.querySelectorAll('[data-rcard]'));
      this.shuffleBtn = this.querySelector('[data-shuffle]');
      this.valuetext = this.getAttribute('data-valuetext') || '';

      this.mql = window.matchMedia ? window.matchMedia(MOBILE) : null;
      this.touchedOnce = false;
      this.timers = [];
      this.dragging = false;
      this.pending = false;
      this.raf = 0;
      this._now = -1;
      this.setBounds();
      this.p = this.restP();
      this.target = this.p;
      this.v = 0;

      this.tick = this.tick.bind(this);
      this.onDown = this.onDown.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onUp = this.onUp.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onSwitch = this.onSwitch.bind(this);
      this.onBreakpoint = this.onBreakpoint.bind(this);
      this.onMotion = this.onMotion.bind(this);
      this.replay = this.replay.bind(this);

      this.stage.addEventListener('pointerdown', this.onDown);
      this.stage.addEventListener('pointermove', this.onMove);
      this.stage.addEventListener('pointerup', this.onUp);
      this.stage.addEventListener('pointercancel', this.onCancel);
      this.stage.addEventListener('lostpointercapture', this.onCancel);
      this.stage.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });
      this.handle.addEventListener('keydown', this.onKey);
      var self = this;
      this.switchBtns.forEach(function (btn) {
        btn.addEventListener('click', self.onSwitch);
      });
      if (this.mql) {
        if (this.mql.addEventListener) this.mql.addEventListener('change', this.onBreakpoint);
        else if (this.mql.addListener) this.mql.addListener(this.onBreakpoint);
      }
      document.addEventListener('frost:motion-change', this.onMotion);

      this.measure();
      this.classList.add('is-live');
      this.render(true);

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(function () {
          self.measure();
          self.render(true);
        });
        this.ro.observe(this.stage);
      }

      if ('IntersectionObserver' in window && this.getAttribute('data-hint') !== 'false') {
        this.hintIo = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              self.hintIo.disconnect();
              self.hintIo = null;
              self.nudge();
            });
          },
          { threshold: 0.55 }
        );
        this.hintIo.observe(this.stage);
      }

      this.setupRiffle();
    }

    disconnectedCallback() {
      if (!this._bound) return;
      this._bound = false;
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.clearTimers();
      if (this.ro) this.ro.disconnect();
      if (this.hintIo) this.hintIo.disconnect();
      if (this.riffleIo) this.riffleIo.disconnect();
      if (this.mql) {
        if (this.mql.removeEventListener) this.mql.removeEventListener('change', this.onBreakpoint);
        else if (this.mql.removeListener) this.mql.removeListener(this.onBreakpoint);
      }
      document.removeEventListener('frost:motion-change', this.onMotion);
    }

    /* Geometry ---------------------------------------------------------------------------------------- */
    isMobile() {
      return !!(this.mql && this.mql.matches);
    }

    restP() {
      return this.isMobile() ? 0.74 : 0.5;
    }

    setBounds() {
      this.min = this.isMobile() ? 0.1 : 0.12;
      this.max = this.isMobile() ? 0.9 : 0.88;
    }

    measure() {
      this.W = this.stage.clientWidth || 1;
      var el = this.sides[0].el;
      this.cw = (el && el.offsetWidth) || this.W / 2;
    }

    pFromX(clientX) {
      var rect = this.stage.getBoundingClientRect();
      return (clientX - rect.left) / (rect.width || 1);
    }

    /* Rendering -------------------------------------------------------------------------------------- */
    render(force) {
      var W = this.W;
      var p = this.p;
      var regionA = p * W;
      var regionU = W - regionA;

      this.layerU.style.clipPath = 'inset(0 0 0 ' + (p * 100).toFixed(3) + '%)';
      this.handle.style.transform = 'translate3d(' + regionA.toFixed(2) + 'px, 0, 0)';

      this.place(this.sides[0], regionA / 2, regionA, p);
      this.place(this.sides[1], regionA + regionU / 2, regionU, 1 - p);

      var now = Math.round(p * 100);
      if (now !== this._now || force) {
        this._now = now;
        this.handle.setAttribute('aria-valuenow', String(now));
        if (this.valuetext) {
          this.handle.setAttribute(
            'aria-valuetext',
            this.valuetext.replace('[a]', String(now)).replace('[u]', String(100 - now))
          );
        }
        var shown = p >= 0.5 ? 'artig' : 'unartig';
        if (shown !== this._shown || force) {
          this._shown = shown;
          this.switchBtns.forEach(function (btn) {
            btn.setAttribute('aria-pressed', btn.getAttribute('data-show') === shown ? 'true' : 'false');
          });
        }
      }
    }

    place(side, cx, region, share) {
      if (!side.el) return;
      var cw = this.cw;
      side.el.style.transform = 'translate3d(' + (cx - cw / 2).toFixed(2) + 'px, -50%, 0)';

      var grow = smooth(clamp((share - 0.28) / 0.5, 0, 1));
      if (side.visual) side.visual.style.transform = 'scale(' + (0.9 + 0.14 * grow).toFixed(4) + ')';

      if (side.text) {
        var fade = clamp((region - cw * 0.8) / (cw * 0.2), 0, 1);
        side.text.style.opacity = fade.toFixed(3);
        side.text.style.transform = 'translate3d(0, ' + ((1 - fade) * 10).toFixed(2) + 'px, 0)';
      }

      var open = smooth(clamp((share - 0.42) / 0.4, 0, 1));
      var tx = 26 + 46 * open;
      var rot = 8 + 11 * open;
      var ty = -3 * open;
      if (side.frontL) {
        side.frontL.style.transform =
          'translate3d(' + (-tx).toFixed(2) + '%, ' + ty.toFixed(2) + '%, 0) rotate(' + (-rot).toFixed(2) + 'deg)';
      }
      if (side.frontR) {
        side.frontR.style.transform =
          'translate3d(' + tx.toFixed(2) + '%, ' + ty.toFixed(2) + '%, 0) rotate(' + rot.toFixed(2) + 'deg)';
      }
    }

    /* Spring ------------------------------------------------------------------------------------------- */
    moveTo(target, instant) {
      this.target = clamp(target, this.min, this.max);
      if (instant || motion() === 'none') {
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = 0;
        this.p = this.target;
        this.v = 0;
        this.render();
        return;
      }
      if (!this.raf) {
        this.last = performance.now();
        this.raf = requestAnimationFrame(this.tick);
      }
    }

    tick(now) {
      var dt = clamp((now - this.last) / 1000, 0.001, 0.034);
      this.last = now;
      var soft = motion() === 'soft';
      var k = this.dragging ? 520 : 150;
      var c = this.dragging || soft ? 2 * Math.sqrt(k) : 17;
      var a = -k * (this.p - this.target) - c * this.v;
      this.v += a * dt;
      this.p += this.v * dt;
      this.p = clamp(this.p, this.min - 0.04, this.max + 0.04);

      if (!this.dragging && Math.abs(this.v) < 0.0004 && Math.abs(this.p - this.target) < 0.0004) {
        this.p = this.target;
        this.v = 0;
        this.render();
        this.raf = 0;
        return;
      }
      this.render();
      this.raf = requestAnimationFrame(this.tick);
    }

    /* Pointer ----------------------------------------------------------------------------------------- */
    onDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.touched();
      this.pointerId = e.pointerId;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.samples = [{ t: e.timeStamp, x: e.clientX }];
      var onHandle = this.handle.contains(e.target);

      if (e.pointerType === 'mouse') {
        e.preventDefault();
        try {
          this.handle.focus({ preventScroll: true });
        } catch (err) {
          /* ignore */
        }
        this.startP = onHandle ? this.target : this.pFromX(e.clientX);
        this.beginDrag();
        if (!onHandle) this.moveTo(this.startP);
      } else {
        /* touch / pen: wait for a horizontal move, vertical swipes keep scrolling the page */
        this.pending = true;
        this.startP = this.target;
      }
    }

    beginDrag() {
      this.pending = false;
      this.dragging = true;
      this.classList.add('is-dragging');
      try {
        this.stage.setPointerCapture(this.pointerId);
      } catch (err) {
        /* ignore */
      }
    }

    onMove(e) {
      if (e.pointerId !== this.pointerId) return;
      if (this.pending) {
        var dx = Math.abs(e.clientX - this.startX);
        var dy = Math.abs(e.clientY - this.startY);
        if (dx < 6 || dx < dy) return;
        this.beginDrag();
      }
      if (!this.dragging) return;
      this.samples.push({ t: e.timeStamp, x: e.clientX });
      if (this.samples.length > 6) this.samples.shift();
      var W = this.stage.getBoundingClientRect().width || 1;
      this.moveTo(this.startP + (e.clientX - this.startX) / W);
    }

    onUp(e) {
      if (e.pointerId !== this.pointerId) return;
      this.pending = false;
      if (!this.dragging) return;
      this.endDrag();
      var W = this.stage.getBoundingClientRect().width || 1;
      var first = this.samples[0];
      var last = this.samples[this.samples.length - 1];
      var velocity = 0;
      if (last && first && last.t - first.t > 0 && e.timeStamp - last.t < 80) {
        velocity = (last.x - first.x) / W / ((last.t - first.t) / 1000);
      }
      var base = this.startP + (e.clientX - this.startX) / W;
      this.moveTo(base + clamp(velocity, -4, 4) * 0.12);
    }

    onCancel(e) {
      /* touch: the child under the finger holds the implicit capture; when beginDrag() moves it to the stage,
         the child's lostpointercapture bubbles up here – that is a hand-over, not a cancel */
      if (e && e.type === 'lostpointercapture' && e.target !== this.stage) return;
      if (e && e.pointerId !== undefined && e.pointerId !== this.pointerId) return;
      this.pending = false;
      if (!this.dragging) return;
      this.endDrag();
      this.moveTo(this.target);
    }

    endDrag() {
      this.dragging = false;
      this.classList.remove('is-dragging');
      try {
        if (this.stage.hasPointerCapture && this.stage.hasPointerCapture(this.pointerId)) {
          this.stage.releasePointerCapture(this.pointerId);
        }
      } catch (err) {
        /* ignore */
      }
    }

    /* Keyboard + switch ---------------------------------------------------------------------------------- */
    onKey(e) {
      var t = this.target;
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          t -= 0.05;
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          t += 0.05;
          break;
        case 'PageDown':
          t -= 0.2;
          break;
        case 'PageUp':
          t += 0.2;
          break;
        case 'Home':
          t = this.min;
          break;
        case 'End':
          t = this.max;
          break;
        default:
          return;
      }
      e.preventDefault();
      this.touched();
      this.moveTo(t);
    }

    onSwitch(e) {
      var show = e.currentTarget.getAttribute('data-show');
      this.touched();
      var far = this.isMobile() ? 0.78 : 0.8;
      this.moveTo(show === 'unartig' ? 1 - far : far);
    }

    touched() {
      if (this.touchedOnce) return;
      this.touchedOnce = true;
      this.classList.add('is-touched');
      this.clearTimers();
    }

    nudge() {
      if (this.touchedOnce || motion() !== 'full') return;
      var rest = this.restP();
      var self = this;
      this.moveTo(rest + 0.07);
      this.timers.push(
        setTimeout(function () {
          if (!self.touchedOnce) self.moveTo(rest - 0.05);
        }, 520)
      );
      this.timers.push(
        setTimeout(function () {
          if (!self.touchedOnce) self.moveTo(rest);
        }, 1040)
      );
    }

    clearTimers() {
      this.timers.forEach(function (t) {
        clearTimeout(t);
      });
      this.timers = [];
    }

    onBreakpoint() {
      this.setBounds();
      this.measure();
      this.moveTo(this.touchedOnce ? this.target : this.restP(), true);
      this.render(true);
    }

    onMotion() {
      if (motion() === 'none') this.moveTo(this.target, true);
    }

    /* Riffle ---------------------------------------------------------------------------------------------- */
    setupRiffle() {
      if (!this.riffle || !this.rcards.length) return;
      this.cardsData = this.rcards.map(function (el) {
        return {
          el: el,
          side: num(el, '--side'),
          k: num(el, '--k'),
          m: num(el, '--m'),
          jx: num(el, '--jx'),
          jr: num(el, '--jr'),
        };
      });
      this.riffle.addEventListener('click', this.replay);
      if (this.shuffleBtn) this.shuffleBtn.addEventListener('click', this.replay);

      if (motion() !== 'full' || !('IntersectionObserver' in window) || typeof this.riffle.animate !== 'function') return;
      this.riffle.classList.add('is-split');
      var self = this;
      this.riffleIo = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            self.riffleIo.disconnect();
            self.riffleIo = null;
            self.merge();
          });
        },
        { threshold: 0.6 }
      );
      this.riffleIo.observe(this.riffle);
    }

    splitT(d) {
      return 'translate(' + d.side * 92 + '%, ' + -d.k * 2 + 'px) rotate(' + d.side * 6 + 'deg)';
    }

    midT(d) {
      return 'translate(' + d.side * 30 + '%, ' + (-d.m * 1.6 - 26) + 'px) rotate(' + -d.side * 9 + 'deg)';
    }

    finalT(d) {
      return 'translate(' + d.jx + '%, ' + -d.m * 1.6 + 'px) rotate(' + d.jr + 'deg)';
    }

    merge() {
      if (!this.cardsData) return;
      var self = this;
      if (motion() !== 'full') {
        this.riffle.classList.remove('is-split');
        return;
      }
      this.riffling = true;
      var anims = this.cardsData.map(function (d) {
        return d.el.animate(
          [
            { transform: self.splitT(d), easing: EASE_OUT },
            { transform: self.midT(d), offset: 0.45, easing: EASE_IN_OUT },
            { transform: self.finalT(d) },
          ],
          { duration: 640, delay: 120 + d.m * 70, fill: 'backwards' }
        );
      });
      this.riffle.classList.remove('is-split');
      Promise.all(
        anims.map(function (a) {
          return a.finished;
        })
      ).then(
        function () {
          self.riffling = false;
        },
        function () {
          self.riffling = false;
        }
      );
    }

    replay() {
      if (this.riffling || !this.cardsData || motion() !== 'full') return;
      if (typeof this.riffle.animate !== 'function') return;
      this.riffling = true;
      var self = this;
      var anims = this.cardsData.map(function (d) {
        return d.el.animate([{ transform: self.finalT(d) }, { transform: self.splitT(d) }], {
          duration: 420,
          delay: (11 - d.m) * 22,
          easing: EASE_IN_OUT,
          fill: 'forwards',
        });
      });
      Promise.all(
        anims.map(function (a) {
          return a.finished;
        })
      ).then(
        function () {
          self.riffle.classList.add('is-split');
          anims.forEach(function (a) {
            a.cancel();
          });
          self.merge();
        },
        function () {
          self.riffling = false;
        }
      );
    }
  }

  customElements.define('fb-decks', FbDecks);
})();
